import { play } from 'cuelume'
import Decimal from 'decimal.js'

import {
  resolveDenomination,
  resolveEffectiveAmount,
} from '@/features/order/order-form/lib/instrument-resolution'
import { baseCurrencyHint, dedupeTargets } from '@/features/order/order-form/lib/submitters/shared'
import type { SubmitterContext } from '@/features/order/order-form/lib/submitters/types'
import { buildSpreaderParams } from '@/features/spreader/lib/build-spreader-params'
import { validateSpreaderLegs } from '@/features/spreader/lib/validate-spreader-form'
import { SpreaderLegInput } from '@/features/spreader/types'
import { trackOrderItems } from '@/lib/analytics-order'
import { toast } from '@/lib/toast'
import { randomUUID } from '@/lib/utils'
import { getInstrumentMapForExchange } from '@/lib/utils/exchange-instrument-map'
import type { useStartSpreaderAlgo } from '@/service/algo.hook'
import { SpreaderLegRole } from '@/types/generated/algo'
import { OrderSide } from '@/types/generated/common/trading'
import type { Exchange, Instrument } from '@/types/generated/common/types'

export type SubmitSpreaderDeps = {
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
  startSpreader: ReturnType<typeof useStartSpreaderAlgo>['mutateAsync']
}

// Submit Spreader algo order. Unlike TWAP/LimitChase, every leg's side/role is already its
// own row's own state (set by the matrix-cell prefill or the row's role toggle) — the
// outer buy/sell button's `side` is ignored here, same as submitTwapOrder ignores it for
// its own per-row `item.side`.
export async function submitSpreaderOrder(ctx: SubmitterContext, deps: SubmitSpreaderDeps) {
  const { t, form } = ctx
  const { multiExchangeInstrumentMap, startSpreader } = deps
  if (!ctx.validateSubmissionRows()) return
  const rows = ctx.getSubmissionRows()

  if (rows.length === 0) {
    toast.error(t('selectAccountFirst'), { position: 'bottom-right' })
    return
  }

  const slicePctRaw = (form.getValues('spreaderSlicePct') || '').trim()
  const limitPriceRaw = (form.getValues('spreaderLimitPrice') || '').trim()
  // Entered as a PERCENT string (e.g. "1" = 1%, matching PercentFractionInput's own
  // convention elsewhere) — the wire wants a (0,1] fraction.
  let slicePctFraction = slicePctRaw
  try {
    if (slicePctRaw) slicePctFraction = new Decimal(slicePctRaw).dividedBy(100).toString()
  } catch {
    // leave as-is — validateSpreaderLegs below will reject the malformed string
  }

  const legs: SpreaderLegInput[] = rows.map(({ item, rawItem, account, exchange }) => {
    // Resolved against THIS leg's own exchange, not the primary-exchange-only
    // instrumentMap — a spreader leg (e.g. a Deribit option) can live on a different
    // exchange than the ticket's primary one.
    const rowInstruments = getInstrumentMapForExchange(multiExchangeInstrumentMap, exchange)
    const amountUnit = item.amountUnit ?? ''
    // Options are sized in contracts (BASE) whatever the leg's amountUnit says.
    const denomination = resolveDenomination(
      rowInstruments,
      item.symbol,
      item.itemType,
      amountUnit,
      baseCurrencyHint(item.baseCurrency)
    )
    // Options with contractSize > 1 (e.g. TRX: 1 contract = 10,000 TRX) take the UI
    // amount as a contract count — scale to raw quantity, same as
    // submitLimitMarketOrder/submitTwapOrder/submitLimitChaseOrder already do.
    const effectiveAmount = resolveEffectiveAmount(
      rowInstruments,
      item.symbol,
      item.itemType,
      rawItem.amount || 0
    )
    return {
      symbol: item.symbol,
      quantity: effectiveAmount.toString(),
      denomination,
      accountId: account.accountId,
      side: item.side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
      role:
        item.spreaderRole === 'quoting'
          ? SpreaderLegRole.SPREADER_LEG_ROLE_QUOTING
          : SpreaderLegRole.SPREADER_LEG_ROLE_TAKING,
    }
  })

  const validationError = validateSpreaderLegs(legs, slicePctFraction, limitPriceRaw)
  if (validationError) {
    toast.error(t(`spreaderValidation.${validationError}` as Parameters<typeof t>[0]), {
      position: 'bottom-right',
    })
    return
  }

  ctx.setIsSubmitting(true)
  try {
    const spreaderSubmissionId = randomUUID()
    const params = buildSpreaderParams(legs, slicePctFraction, limitPriceRaw)
    await startSpreader({ ...params, correlationId: spreaderSubmissionId })

    trackOrderItems('spreader', legs, ctx.getAnalyticsAccounts(), 'order-form')
    const spreaderTargets = dedupeTargets(legs)
    const selection = ctx.getSelection()
    ctx.triggerOptimistic(
      spreaderSubmissionId,
      spreaderTargets,
      selection.instruments,
      selection.options
    )
    ctx.resetFormAfterOrder()
  } catch (error) {
    toast.error(error instanceof Error ? error.message : t('spreaderSubmissionFailed'), {
      position: 'bottom-right',
    })
    play('error')
    ctx.setIsSubmitting(false)
  }
}
